import React from 'react'
import { motion } from 'framer-motion'

interface HeaderProps {
  title: React.ReactNode
  lastUpdate?: string
  isRefreshing?: boolean
  onRefresh?: () => void
  className?: string
}

export const Header: React.FC<HeaderProps> = ({
  title,
  lastUpdate,
  isRefreshing = false,
  onRefresh,
}) => {
  return (
    <header className="sticky top-0 z-40 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="h-14 sm:h-16 flex items-center justify-between">
          {/* Логотип и название */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {title}
          </div>

          {/* Кнопки и информация */}
          <div className="flex items-center gap-2 sm:gap-3">
            {lastUpdate && (
              <div className="hidden xs:flex items-center gap-2 text-sm">
                <div className="flex items-center gap-2 px-2 sm:px-3 py-1 rounded-lg bg-gray-100/50 dark:bg-gray-700/50">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 truncate max-w-[80px] sm:max-w-none">
                    {lastUpdate}
                  </span>
                </div>
              </div>
            )}

            {onRefresh && (
              <motion.div
                className="relative"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Фоновая анимация */}
                <motion.div
                  className="absolute inset-0 rounded-lg overflow-hidden"
                  initial={false}
                  animate={isRefreshing ? {
                    opacity: [0.3, 0.6, 0.3],
                  } : {
                    opacity: 0,
                  }}
                  transition={isRefreshing ? {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  } : {}}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-blue-600/20" />
                </motion.div>

                {/* Основная кнопка */}
                <motion.button
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className="relative flex items-center justify-center gap-1.5 xs:gap-2 px-3 xs:px-4 py-1.5 xs:py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium transition-all duration-300 shadow-sm hover:shadow-md disabled:shadow-none overflow-hidden group min-w-[90px] xs:min-w-[110px]"
                  style={{
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)'
                  }}
                  whileHover={!isRefreshing ? {
                    scale: 1.02,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  } : {}}
                >
                  {/* Блестящий эффект при hover */}
                  <motion.div
                    className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.6 }}
                  />

                  {/* Содержимое кнопки */}
                  <div className="flex items-center gap-1.5 xs:gap-2.5">
                    {/* Анимированная иконка */}
                    {isRefreshing ? (
                      <>
                        <motion.div
                          className="relative flex-shrink-0"
                          animate={{ rotate: 360 }}
                          transition={{ 
                            duration: 2, 
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        >
                          {/* Внешнее кольцо */}
                          <motion.div
                            className="w-3.5 h-3.5 xs:w-5 xs:h-5 rounded-full border border-transparent"
                            animate={{
                              borderTopColor: ['rgba(255,255,255,0.8)', 'rgba(255,255,255,0.3)', 'rgba(255,255,255,0.8)'],
                              borderRightColor: ['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.8)', 'rgba(255,255,255,0.3)'],
                              borderBottomColor: ['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.3)', 'rgba(255,255,255,0.3)'],
                              borderLeftColor: ['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.3)', 'rgba(255,255,255,0.8)'],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "linear"
                            }}
                          />
                          
                          {/* Внутренняя точка */}
                          <motion.div
                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-0.5 h-0.5 xs:w-1 xs:h-1 rounded-full bg-white"
                            animate={{
                              scale: [1, 1.3, 1],
                              opacity: [0.8, 1, 0.8]
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          />
                        </motion.div>
                        
                        {/* Текст с плавной сменой */}
                        <motion.span
                          key="updating"
                          initial={{ opacity: 0, y: 3 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -3 }}
                          className="font-medium tracking-wide text-xs xs:text-sm truncate max-w-[60px] xs:max-w-none"
                        >
                          Обновление...
                        </motion.span>
                      </>
                    ) : (
                      <>
                        <motion.div
                          className="relative flex-shrink-0"
                          whileHover={{ 
                            rotate: 180,
                            transition: { 
                              duration: 0.4,
                              ease: "easeInOut"
                            }
                          }}
                        >
                          {/* Статичная иконка */}
                          <motion.div
                            className="text-sm xs:text-lg"
                            animate={{ 
                              scale: [1, 1.1, 1],
                              transition: { 
                                duration: 3,
                                repeat: Infinity,
                                repeatDelay: 2
                              }
                            }}
                          >
                            🔄
                          </motion.div>
                          
                          {/* Свечение */}
                          <motion.div
                            className="absolute inset-0 rounded-full bg-white/10"
                            animate={{
                              scale: [1, 1.3, 1],
                              opacity: [0, 0.3, 0]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              repeatDelay: 1
                            }}
                          />
                        </motion.div>
                        
                        <motion.span
                          className="font-medium tracking-wide text-xs xs:text-sm truncate max-w-[40px] xs:max-w-none"
                          whileHover={{ scale: 1.02 }}
                        >
                          Обновить
                        </motion.span>
                      </>
                    )}
                  </div>
                </motion.button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}